import { Component, OnInit, ChangeDetectorRef, ChangeDetectionStrategy, signal } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { MultiSelectModule } from 'primeng/multiselect';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { BillingService } from '@generated/services/billing.service';
import type { InvoiceDto } from '@generated/models/invoice-dto';
import { InvoiceStatus, PaymentStatus } from '@generated/models';
import { formatAmount } from '@shared/utils/currency';

@Component({
  selector: 'app-invoices',
  imports: [FormsModule, TableModule, ButtonModule, ProgressSpinnerModule, TagModule, TooltipModule, MultiSelectModule, ToggleSwitchModule],
  templateUrl: './invoices.html',
  styleUrl: './invoices.css',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class Invoices implements OnInit {
  invoices = signal<InvoiceDto[]>([]);
  isLoading = signal(false);
  generatingPaymentUrl = signal<string | null>(null);

  // Filters
  selectedStatuses: InvoiceStatus[] = [];
  selectedPaymentStatuses: PaymentStatus[] = [];
  showOverdue: boolean | null = null;

  // Filter options
  statusOptions = [
    { label: $localize`:@@invoices.status.draft:Draft`, value: 'Draft' as InvoiceStatus },
    { label: $localize`:@@campaigns.status.failed:Failed`, value: 'Failed' as InvoiceStatus },
    { label: $localize`:@@invoices.status.finalized:Finalized`, value: 'Finalized' as InvoiceStatus },
    { label: $localize`:@@conversations.status.pending:Pending`, value: 'Pending' as InvoiceStatus },
    { label: $localize`:@@invoices.status.voided:Voided`, value: 'Voided' as InvoiceStatus }
  ];

  paymentStatusOptions = [
    { label: $localize`:@@conversations.status.pending:Pending`, value: 'Pending' as PaymentStatus },
    { label: $localize`:@@invoices.payment.succeeded:Succeeded`, value: 'Succeeded' as PaymentStatus },
    { label: $localize`:@@campaigns.status.failed:Failed`, value: 'Failed' as PaymentStatus }
  ];

  readonly unknownStatusLabel = $localize`:@@conversations.status.unknown:Unknown`;
  readonly overdueLabel = $localize`:@@invoices.overdue:Overdue`;

  getPaymentStatusLabel(status: string | null | undefined): string {
    if (!status) return this.unknownStatusLabel;
    return this.paymentStatusOptions.find(o => o.value === status)?.label ?? status;
  }

  constructor(
    private billingService: BillingService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.loadInvoices();
  }

  onFilterChange(): void {
    this.loadInvoices();
  }

  private loadInvoices(): void {
    this.isLoading.set(true);
    this.cdr.markForCheck();

    this.billingService.apiBillingInvoicesGet({
      statuses: this.selectedStatuses.length > 0 ? this.selectedStatuses : undefined,
      paymentStatuses: this.selectedPaymentStatuses.length > 0 ? this.selectedPaymentStatuses : undefined,
      paymentOverdue: this.showOverdue === true ? true : undefined
    }).subscribe({
      next: (result) => {
        this.invoices.set(result.invoices || []);
        this.isLoading.set(false);
        this.cdr.markForCheck();
      },
      error: (error) => {
        console.error('Failed to load invoices:', error);
        this.isLoading.set(false);
        this.cdr.markForCheck();
      }
    });
  }

  formatAmount(amountCents: number | undefined, currency: string | null | undefined): string {
    if (amountCents === undefined || amountCents === null) return '0.00';
    return formatAmount(amountCents / 100, currency);
  }

  formatDate(dateString: string | undefined | null): string {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  }

  getPaymentStatusSeverity(paymentStatus: string | null | undefined): 'success' | 'info' | 'warn' | 'danger' | 'secondary' | 'contrast' | undefined {
    if (!paymentStatus) return 'secondary';
    const lowerStatus = paymentStatus.toLowerCase();
    if (lowerStatus === 'succeeded' || lowerStatus === 'paid') return 'success';
    if (lowerStatus === 'pending') return 'warn';
    if (lowerStatus === 'failed') return 'danger';
    return 'secondary';
  }

  downloadInvoice(invoice: InvoiceDto): void {
    if (invoice.file_url) {
      window.open(invoice.file_url, '_blank');
    }
  }

  shouldShowPayButton(invoice: InvoiceDto): boolean {
    const paymentStatus = invoice.payment_status?.toLowerCase();
    const status = invoice.status?.toLowerCase();

    // Don't show button if status is voided
    if (status === 'voided') {
      return false;
    }

    // Show pay button if payment status is not success
    return paymentStatus !== 'succeeded' && paymentStatus !== 'paid';
  }

  payInvoice(invoice: InvoiceDto): void {
    if (!invoice.lago_id) {
      console.error('Invoice lago_id is missing');
      return;
    }

    this.generatingPaymentUrl.set(invoice.lago_id);
    this.cdr.markForCheck();

    this.billingService.apiBillingInvoicesLagoInvoiceIdPaymentUrlPost({
      lagoInvoiceId: invoice.lago_id
    }).subscribe({
      next: (result) => {
        this.generatingPaymentUrl.set(null);
        if (result.payment_url) {
          window.open(result.payment_url, '_blank');
        } else if (result.error_message) {
          console.error('Failed to generate payment URL:', result.error_message);
        }
        this.cdr.markForCheck();
      },
      error: (error) => {
        console.error('Failed to generate payment URL:', error);
        this.generatingPaymentUrl.set(null);
        this.cdr.markForCheck();
      }
    });
  }
}
